import api from './api';

/**
 * Service for appointment-related API calls
 * All methods return { success: true, data } or { success: false, error }
 */

/**
 * Helper to extract a clean error message from API errors
 */
const parseErrorResponse = (error, defaultMessage) => {
  if (!error.response?.data) {
    return defaultMessage;
  }

  const data = error.response.data;

  // Check for HTML response (server error page)
  if (typeof data === 'string' && data.includes('<')) {
    return `Server error (${error.response.status || 'unknown'})`;
  }

  // JSON object response
  if (typeof data === 'object') {
    return data.detail || data.error || data.message || JSON.stringify(data);
  }

  return data;
};

/**
 * Helper to validate that response data is valid JSON (not HTML)
 */
const isValidResponse = (data) => {
  if (typeof data === 'string' && data.includes('<html')) {
    return false;
  }
  return data !== null && data !== undefined;
};

const appointmentService = {
  // Customer endpoints

  /**
   * Fetch customer's appointments
   */
  async fetchMyAppointments() {
    try {
      const response = await api.get('/services/appointments/');
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching appointments:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch appointments'
      };
    }
  },

  /**
   * Create a new appointment
   * @param {Object} appointmentData - { service, pet, appointment_date, start_time, customer_notes }
   */
  async createAppointment(appointmentData) {
    try {
      const response = await api.post('/services/appointments/', appointmentData);
      if (!isValidResponse(response.data)) {
        return {
          success: false,
          error: 'Invalid server response. Please try again.'
        };
      }
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error creating appointment:', error);
      return {
        success: false,
        error: parseErrorResponse(error, 'Failed to create appointment')
      };
    }
  },

  /**
   * Fetch appointment detail
   * @param {number} appointmentId
   */
  async fetchAppointmentDetail(appointmentId) {
    try {
      const response = await api.get(`/services/appointments/${appointmentId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching appointment detail:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch appointment details'
      };
    }
  },

  /**
   * Cancel an appointment (customer can only cancel PENDING appointments)
   * @param {number} appointmentId
   */
  async cancelAppointment(appointmentId) {
    try {
      const response = await api.delete(`/services/appointments/${appointmentId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error cancelling appointment:', error);
      return {
        success: false,
        error: error.response?.data?.error || error.response?.data?.detail || 'Failed to cancel appointment'
      };
    }
  },

  // Partner endpoints

  /**
   * Fetch partner's appointments with optional filters
   * @param {Object} params - Query parameters (status, appointment_date, service)
   */
  async fetchPartnerAppointments(params = {}) {
    try {
      const response = await api.get('/services/partner/appointments/', { params });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching partner appointments:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch partner appointments'
      };
    }
  },

  /**
   * Fetch partner appointment detail
   * @param {number} appointmentId
   */
  async fetchPartnerAppointmentDetail(appointmentId) {
    try {
      const response = await api.get(`/services/partner/appointments/${appointmentId}/`);
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error fetching partner appointment detail:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Failed to fetch appointment details'
      };
    }
  },

  /**
   * Update appointment status (partner only)
   * @param {number} appointmentId
   * @param {Object} updateData - { status, partner_notes }
   */
  async updateAppointmentStatus(appointmentId, updateData) {
    try {
      const response = await api.patch(`/services/partner/appointments/${appointmentId}/`, updateData);
      // Validate response is valid JSON, not HTML error page
      if (!isValidResponse(response.data)) {
        return {
          success: false,
          error: 'Invalid server response. Please try again.'
        };
      }
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Error updating appointment status:', error);
      return {
        success: false,
        error: parseErrorResponse(error, 'Failed to update appointment status')
      };
    }
  }
};

export default appointmentService;
